import React, { useEffect, useRef, useState } from 'react';
import { fetchSupportAttachmentBlob } from '../../services/supportApi';
import { useAppSelector } from '../../store/hooks';
import { selectAuth } from '../../store/authSlice';
import { selectSupportThread } from '../../store/supportChatSlice';
import type { SupportAttachmentDTO } from '../../types/support';
import {
  formatSupportBytes,
  resolveAttachmentKind,
} from '../../utils/supportAttachments';
import { resolveSupportAttachmentBlob } from '../../utils/supportChatMediaSync';
import SupportVoicePlayer from './SupportVoicePlayer';

interface Props {
  attachment: SupportAttachmentDTO;
  /** object URL для оптимистичного превью (без fetch) */
  directUrl?: string | null;
  onOpenLightbox?: (url: string) => void;
  variant?: 'user' | 'admin';
}

/**
 * Рендер вложения по `kind` (image / video / audio / file).
 * Медиа загружаются через authorized fetch → blob URL.
 */
const SupportAttachmentView: React.FC<Props> = ({
  attachment,
  directUrl = null,
  onOpenLightbox,
  variant = 'admin',
}) => {
  const { user } = useAppSelector(selectAuth);
  const thread = useAppSelector(selectSupportThread);
  const [src, setSrc] = useState<string | null>(directUrl);
  const [loading, setLoading] = useState<boolean>(!directUrl);
  const [error, setError] = useState<string | null>(null);
  const cancelledRef = useRef(false);
  const createdUrlRef = useRef<string | null>(null);

  const kind = resolveAttachmentKind(attachment);
  const displayName = attachment.originalName || 'Файл';

  useEffect(() => {
    cancelledRef.current = false;
    if (directUrl) {
      setSrc(directUrl);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    const load = async () => {
      try {
        let blob: Blob;
        if (user?.userId) {
          blob = await resolveSupportAttachmentBlob(
            user.userId,
            thread?.id ?? null,
            attachment,
          );
        } else {
          blob = await fetchSupportAttachmentBlob(attachment.id);
        }

        if (cancelledRef.current) return;

        const objectUrl = URL.createObjectURL(blob);
        createdUrlRef.current = objectUrl;
        setSrc(objectUrl);
        setLoading(false);
      } catch (err: unknown) {
        if (cancelledRef.current) return;
        const message = err instanceof Error ? err.message : 'Не удалось загрузить вложение';
        setError(message);
        setLoading(false);
      }
    };

    void load();

    return () => {
      cancelledRef.current = true;
      if (createdUrlRef.current) {
        try {
          URL.revokeObjectURL(createdUrlRef.current);
        } catch {}
        createdUrlRef.current = null;
      }
    };
  }, [attachment, directUrl, thread?.id, user?.userId]);

  if (loading) {
    return (
      <div className="sc-attachment sc-attachment--loading" aria-label="Загрузка вложения">
        <div className="sc-attachment__spinner" />
      </div>
    );
  }

  if (error || !src) {
    return (
      <div className="sc-attachment sc-attachment--error" role="alert">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <span>Ошибка загрузки</span>
      </div>
    );
  }

  switch (kind) {
    case 'image':
      return (
        <button
          type="button"
          className="sc-attachment sc-attachment--image"
          onClick={() => onOpenLightbox?.(src)}
          aria-label={`Открыть ${displayName}`}
        >
          <img src={src} alt={displayName} loading="lazy" draggable={false} />
        </button>
      );

    case 'video':
      return (
        <div className="sc-attachment sc-attachment--video">
          <video src={src} controls playsInline preload="metadata">
            <track kind="captions" />
          </video>
          {attachment.durationSec != null && (
            <span className="sc-attachment__duration">{formatDuration(attachment.durationSec)}</span>
          )}
        </div>
      );

    case 'voice':
    case 'audio':
      return (
        <div className="sc-attachment sc-attachment--voice">
          <SupportVoicePlayer
            src={src}
            durationSec={attachment.durationSec}
            variant={variant}
            seed={attachment.id}
          />
        </div>
      );

    case 'file':
    default:
      return (
        <a
          className="sc-attachment sc-attachment--file sc-attachment--download"
          href={src}
          download={displayName}
          target="_blank"
          rel="noopener noreferrer"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="12" y1="18" x2="12" y2="12" />
            <polyline points="9 15 12 18 15 15" />
          </svg>
          <span className="sc-attachment__file-info">
            <span className="sc-attachment__file-name">{displayName}</span>
            <span className="sc-attachment__file-size">{formatSupportBytes(attachment.size)}</span>
          </span>
        </a>
      );
  }
};

const formatDuration = (sec: number): string => {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

export default SupportAttachmentView;
